import React, { useCallback, useEffect, useRef, useState } from "react";
import i18n from "i18next";
import { LuCheck, LuTriangleAlert } from "react-icons/lu";
import { getConvertJobStatus } from "../../../../common/server_utils.es6";

// Background tracker for the "Farklı Oranda Üret" (convert-aspect-ratio) job.
//
// RatioReproduceModal fires a `ratioReproduceStarted` window event right after
// the convert POST resolves (carrying { jobId, newTrainingId, name }) — or, when
// the POST itself fails, an event with { failed: true, error, name } — then
// closes. This component is mounted once in EditorModals (which outlives the
// modal), polls GET api/jobs/{jobId} until the job finishes, and reports the
// outcome as a bottom-right toast: progress while running, success or error on
// finish.

export const RATIO_REPRODUCE_EVENT = "ratioReproduceStarted";

const POLL_INTERVAL = 5000;
const DONE_DISMISS_MS = 10000;
const MAX_POLL_ATTEMPTS = 400; // ~33 min at 5s

const RatioReproduceJobTracker = () => {
  // { [key]: { key, jobId, name, status, progress, message, error } }
  const [jobs, setJobs] = useState({});
  const timersRef = useRef({});
  const startedRef = useRef(new Set());
  const mountedRef = useRef(true);
  const counterRef = useRef(0);

  const patchJob = useCallback((key, patch) => {
    setJobs((prev) => (prev[key] ? { ...prev, [key]: { ...prev[key], ...patch } } : prev));
  }, []);

  const removeJob = useCallback((key) => {
    setJobs((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const finish = useCallback(
    (key, status, extra) => {
      if (timersRef.current[key]) {
        clearTimeout(timersRef.current[key]);
        delete timersRef.current[key];
      }
      patchJob(key, { status, progress: status === "completed" ? 100 : undefined, ...extra });
      if (status === "completed") {
        timersRef.current[`${key}__dismiss`] = setTimeout(() => {
          if (mountedRef.current) removeJob(key);
        }, DONE_DISMISS_MS);
      }
    },
    [patchJob, removeJob]
  );

  const pollJob = useCallback(
    (key, jobId) => {
      let attempts = 0;
      const tick = async () => {
        if (!mountedRef.current) return;
        attempts += 1;
        if (attempts > MAX_POLL_ATTEMPTS) {
          finish(key, "failed", {
            error: i18n.t("ratio_reproduce.timeout", {
              defaultValue: "İşlem zaman aşımına uğradı.",
            }),
          });
          return;
        }
        try {
          const data = await getConvertJobStatus(jobId);
          if (!mountedRef.current) return;
          const status = data && data.status;
          patchJob(key, {
            progress: typeof data.progress === "number" ? data.progress : undefined,
            message: data && data.message ? data.message : undefined,
          });
          if (status === "completed") {
            finish(key, "completed", {});
            return;
          }
          if (status === "failed") {
            finish(key, "failed", {
              error:
                (data && data.message) ||
                i18n.t("ratio_reproduce.toast_failed", {
                  defaultValue: "Üretim başarısız oldu.",
                }),
            });
            return;
          }
        } catch (err) {
          // network blip — keep polling
        }
        timersRef.current[key] = setTimeout(tick, POLL_INTERVAL);
      };
      tick();
    },
    [finish, patchJob]
  );

  useEffect(() => {
    const onStart = (e) => {
      const d = (e && e.detail) || {};
      const key = `rr_${counterRef.current++}`;

      // POST failed → immediate error toast, no polling.
      if (d.failed) {
        setJobs((prev) => ({
          ...prev,
          [key]: {
            key,
            name: d.name || "",
            status: "failed",
            error:
              d.error ||
              i18n.t("ratio_reproduce.error", {
                defaultValue: "İşlem başlatılamadı. Lütfen tekrar deneyin.",
              }),
          },
        }));
        return;
      }

      if (!d.jobId || startedRef.current.has(d.jobId)) return;
      startedRef.current.add(d.jobId);
      setJobs((prev) => ({
        ...prev,
        [key]: {
          key,
          jobId: d.jobId,
          name: d.name || "",
          status: "processing",
          progress: 0,
        },
      }));
      pollJob(key, d.jobId);
    };
    window.addEventListener(RATIO_REPRODUCE_EVENT, onStart);
    return () => window.removeEventListener(RATIO_REPRODUCE_EVENT, onStart);
  }, [pollJob]);

  useEffect(() => {
    mountedRef.current = true;
    const timers = timersRef.current;
    return () => {
      mountedRef.current = false;
      Object.values(timers).forEach((t) => clearTimeout(t));
    };
  }, []);

  const jobList = Object.values(jobs);
  if (jobList.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 1000001,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        maxWidth: 360,
      }}
    >
      <style>{`@keyframes ratio-reproduce-job-spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
      {jobList.map((job) => {
        const isDone = job.status === "completed";
        const isFailed = job.status === "failed";
        const accent = isFailed ? "#dc2626" : isDone ? "#16a34a" : "#6d5dfc";

        let text;
        if (isFailed) {
          text = job.error || i18n.t("ratio_reproduce.toast_failed") || "Üretim başarısız oldu.";
        } else if (isDone) {
          text =
            i18n.t("ratio_reproduce.toast_done", { defaultValue: "Yeni eğitim oluşturuldu." }) ||
            "Yeni eğitim oluşturuldu.";
        } else {
          text =
            job.message ||
            i18n.t("ratio_reproduce.toast_progress", {
              progress: job.progress || 0,
              defaultValue: `Üretiliyor... %${job.progress || 0}`,
            });
        }

        const title =
          isDone || isFailed
            ? i18n.t("ratio_reproduce.toast_done_title", { defaultValue: "Farklı Oranda Üret" })
            : i18n.t("ratio_reproduce.toast_started_title", {
                defaultValue: "Farklı oranda üretim başlatıldı",
              });

        return (
          <div
            key={job.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 10,
              background: "#fff",
              border: `1px solid ${isFailed ? "#fecaca" : isDone ? "#bbf7d0" : "#e2e8f0"}`,
              boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
            }}
          >
            <span
              style={{
                flex: "0 0 auto",
                width: 24,
                height: 24,
                display: "grid",
                placeItems: "center",
                color: accent,
              }}
            >
              {isDone ? (
                <LuCheck size={18} />
              ) : isFailed ? (
                <LuTriangleAlert size={18} />
              ) : (
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    border: "2px solid #e2e8f0",
                    borderTopColor: accent,
                    display: "inline-block",
                    animation: "ratio-reproduce-job-spin 0.8s linear infinite",
                  }}
                />
              )}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{title}</div>
              <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>
                {job.name ? (
                  <span style={{ display: "block", fontWeight: 600, color: "#334155" }}>
                    {job.name}
                  </span>
                ) : null}
                {text}
              </div>
            </div>
            {(isDone || isFailed) && (
              <button
                type="button"
                onClick={() => removeJob(job.key)}
                aria-label="Close"
                style={{
                  flex: "0 0 auto",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 18,
                  lineHeight: 1,
                  color: "#94a3b8",
                  padding: 0,
                }}
              >
                ×
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RatioReproduceJobTracker;
